import { useState } from "react";
import { UserOrgRoleRole } from "../../../../generated/core-api";

export interface PendingOrgInviteBulk {
  email: string;
  name: string;
  role: UserOrgRoleRole;
}

export type OrgInviteBulkCsvParseResult = {
  success: boolean;
  data?: PendingOrgInviteBulk[];
  errors?: string[];
};

function validateHeaders(headers: string[]): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  const emailIndex = headers.indexOf("email");
  const nameIndex = headers.indexOf("name");
  const roleIndex = headers.indexOf("role");

  if (emailIndex === -1 || nameIndex === -1 || roleIndex === -1) {
    errors.push("CSV must contain columns: email, name, role");
    return { isValid: false, errors };
  }

  return { isValid: true, errors: [] };
}

function validateEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function validateRole(role: string): role is UserOrgRoleRole {
  return role === "admin" || role === "member";
}

function parseRow(
  values: string[],
  headerIndices: { email: number; name: number; role: number },
  rowNumber: number
): { data?: PendingOrgInviteBulk; errors: string[] } {
  const errors: string[] = [];

  if (
    values.length <
    Math.max(headerIndices.email, headerIndices.name, headerIndices.role) + 1
  ) {
    errors.push(`Row ${rowNumber}: Not enough columns`);
    return { errors };
  }

  const email = values[headerIndices.email]?.trim();
  const name = values[headerIndices.name]?.trim();
  const role = values[headerIndices.role]?.trim();

  if (!email) {
    errors.push(`Row ${rowNumber}: Email is required`);
  } else if (!validateEmail(email)) {
    errors.push(`Row ${rowNumber}: Invalid email format`);
  }

  if (!name) {
    errors.push(`Row ${rowNumber}: Name is required`);
  }

  if (!role) {
    errors.push(`Row ${rowNumber}: Role is required`);
  } else if (!validateRole(role)) {
    errors.push(
      `Row ${rowNumber}: Role must be 'admin' or 'member', got '${role}'`
    );
  }

  if (errors.length > 0) {
    return { errors };
  }

  return {
    data: {
      email,
      name,
      role: role as UserOrgRoleRole,
    },
    errors: [],
  };
}

export function parseCSVText(text: string): OrgInviteBulkCsvParseResult {
  const lines = text.trim().split("\n");

  if (lines.length < 2) {
    return {
      success: false,
      errors: ["CSV file must contain a header row and at least one data row"],
    };
  }

  const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
  const headerValidation = validateHeaders(headers);

  if (!headerValidation.isValid) {
    return { success: false, errors: headerValidation.errors };
  }

  const headerIndices = {
    email: headers.indexOf("email"),
    name: headers.indexOf("name"),
    role: headers.indexOf("role"),
  };

  const data: PendingOrgInviteBulk[] = [];
  const allErrors: string[] = [];

  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(",").map((v) => v.trim());
    const rowResult = parseRow(values, headerIndices, i + 1);

    if (rowResult.data) {
      data.push(rowResult.data);
    }

    allErrors.push(...rowResult.errors);
  }

  return {
    success: allErrors.length === 0,
    data,
    errors: allErrors.length > 0 ? allErrors : undefined,
  };
}

export function useOrgInviteBulkCsvParser() {
  const [isProcessing, setIsProcessing] = useState(false);

  const parseFile = async (
    file: File
  ): Promise<OrgInviteBulkCsvParseResult> => {
    setIsProcessing(true);

    try {
      const text = await file.text();
      return parseCSVText(text);
    } catch (error) {
      return {
        success: false,
        errors: ["Failed to read file: " + (error as Error).message],
      };
    } finally {
      setIsProcessing(false);
    }
  };

  return {
    parseFile,
    isProcessing,
  };
}
